"use client";

import { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";

interface BookData {
  name: string;
  slug: string;
  canonicalUrl: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  createdAt: string;
}

export default function UpdateBookPage() {
  const navigate = useNavigate();
  const { librarySlug, bookSlug } = useParams();

  const [bookData, setBookData] = useState<BookData>({
    name: "",
    slug: "",
    canonicalUrl: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    createdAt: new Date().toISOString(),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const [chapters, setChapters] = useState<{ id: string; title: string }[]>([]);

  // ---------------------------- Fetch book data ----------------------------
  useEffect(() => {
    if (!librarySlug || !bookSlug) return;

    const fetchBook = async () => {
      setIsLoading(true);
      try {
        const snap = await getDoc(doc(fireDB, "NewBlogs", librarySlug, "books", bookSlug));
        if (!snap.exists()) {
          toast.error("Book not found!");
          return;
        }
        const data = snap.data();
        setBookData({
          name: data.name || "",
          slug: data.slug || "",
          canonicalUrl: data.canonicalUrl || "",
          metaTitle: data.metaTitle || "",
          metaDescription: data.metaDescription || "",
          metaKeywords: data.metaKeywords || "",
          createdAt: data.createdAt || new Date().toISOString(),
        });
      } catch (err) {
        console.error(err);
        toast.error("Error fetching book data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBook();
  }, [librarySlug, bookSlug]);

  // ---------------------------- Fetch chapters ----------------------------
  useEffect(() => {
    if (!librarySlug || !bookSlug) return;

    const fetchChapters = async () => {
      try {
        const snapshot = await getDocs(
          collection(fireDB, "NewBlogs", librarySlug, "books", bookSlug, "chapters")
        );

        // SHOW ONLY title instead of contentTitle
        setChapters(snapshot.docs.map((doc) => ({
          id: doc.id,
          title: doc.data().title || "Untitled",
        })));

      } catch (err) {
        console.error(err);
        toast.error("Error fetching chapters");
      }
    };

    fetchChapters();
  }, [librarySlug, bookSlug]);

  // ---------------------------- Auto-generate slug & canonical URL ----------------------------
  useEffect(() => {
    if (bookData.name && !isSlugEdited) {
      const slug = bookData.name.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
      setBookData((prev) => ({ ...prev, slug }));
    }
  }, [bookData.name, isSlugEdited]);

  useEffect(() => {
    if (bookData.slug) {
      setBookData((prev) => ({ ...prev, canonicalUrl: `https://forumde.in/${prev.slug}` }));
    }
  }, [bookData.slug]);

  // ---------------------------- Update book ----------------------------
  const updateBook = async () => {
    if (!bookData.name || !bookData.slug) {
      toast.error("Name and slug are required!");
      return;
    }

    setIsLoading(true);
    try {
      const oldRef = doc(fireDB, "NewBlogs", librarySlug!, "books", bookSlug!);
      const oldSnap = await getDoc(oldRef);
      if (!oldSnap.exists()) {
        toast.error("Original book not found!");
        setIsLoading(false);
        return;
      }

      const dataToSave = {
        ...bookData,
        seo: {
          title: bookData.metaTitle || bookData.name,
          description: bookData.metaDescription,
          keywords: bookData.metaKeywords.split(",").map((k) => k.trim()),
          canonicalUrl: bookData.canonicalUrl,
        },
        time: Timestamp.now(),
      };

      // Slug changed
      if (bookData.slug !== bookSlug) {
        const newRef = doc(fireDB, "NewBlogs", librarySlug!, "books", bookData.slug);
        await setDoc(newRef, dataToSave);

        const chaptersSnap = await getDocs(collection(fireDB, "NewBlogs", librarySlug!, "books", bookSlug!, "chapters"));
        for (const chap of chaptersSnap.docs) {
          await setDoc(doc(fireDB, "NewBlogs", librarySlug!, "books", bookData.slug, "chapters", chap.id), chap.data());
        }

        await deleteDoc(oldRef);
        toast.success("Book updated and slug renamed!");
        navigate(`/updatebook/${librarySlug}/${bookData.slug}`);
      } else {
        await setDoc(oldRef, dataToSave, { merge: true });
        toast.success("Book updated successfully!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating book");
    } finally {
      setIsLoading(false);
    }
  };

  const goToAddChapter = () =>
    navigate(`/createLibrary/${librarySlug}/create-book/${bookData.slug}/create-chapter`);

  const goToUpdateChapter = (id: string) =>
    navigate(`/updatechapter/${librarySlug}/${bookSlug}/${id}`);

  const deleteChapter = async (id: string) => {
    if (!confirm("Are you sure you want to delete this chapter?")) return;
    try {
      await deleteDoc(doc(fireDB, "NewBlogs", librarySlug!, "books", bookSlug!, "chapters", id));
      setChapters(chapters.filter((c) => c.id !== id));
      toast.success("Chapter deleted!");
    } catch (err) {
      console.error(err);
      toast.error("Error deleting chapter");
    }
  };

  // ---------------------------- UI ----------------------------
  return (
    <div className="bg-gray-50 min-h-screen py-10 px-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-6">📚 Update Book</h1>

        {/* Book Info */}
        <div className="bg-white p-6 rounded-2xl shadow-lg border space-y-4">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Book Information</h2>
          <input
            className="w-full border p-3 rounded-lg"
            placeholder="Book Name"
            value={bookData.name}
            onChange={(e) => setBookData({ ...bookData, name: e.target.value })}
          />
          <input
            className="w-full border p-3 rounded-lg"
            placeholder="Slug"
            value={bookData.slug}
            onChange={(e) => { setBookData({ ...bookData, slug: e.target.value }); setIsSlugEdited(true); }}
          />
          <input
            className="w-full border p-3 rounded-lg bg-gray-100 cursor-not-allowed"
            placeholder="Canonical URL"
            value={bookData.canonicalUrl}
            readOnly
          />
        </div>

        {/* SEO Info */}
        <div className="bg-white p-6 rounded-2xl shadow-lg border space-y-4">
          <h2 className="text-2xl font-semibold text-yellow-700 mb-4">SEO Settings</h2>
          <input
            className="w-full border p-3 rounded-lg"
            placeholder="Meta Title"
            value={bookData.metaTitle}
            onChange={(e) => setBookData({ ...bookData, metaTitle: e.target.value })}
          />
          <textarea
            className="w-full border p-3 rounded-lg"
            rows={3}
            placeholder="Meta Description"
            value={bookData.metaDescription}
            onChange={(e) => setBookData({ ...bookData, metaDescription: e.target.value })}
          />
          <input
            className="w-full border p-3 rounded-lg"
            placeholder="Meta Keywords"
            value={bookData.metaKeywords}
            onChange={(e) => setBookData({ ...bookData, metaKeywords: e.target.value })}
          />
        </div>

        {/* Buttons */}
        <div className="space-y-4">
          <button
            onClick={updateBook}
            disabled={isLoading}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-all"
          >
            {isLoading ? "Updating..." : "Update Book"}
          </button>

          <button
            onClick={goToAddChapter}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-all"
          >
            ➕ Add Chapter
          </button>
        </div>

        {/* Chapters List */}
        <div className="bg-gray-100 p-6 rounded-2xl space-y-3">
          <h2 className="text-2xl font-semibold mb-4">Chapters</h2>

          {chapters.length === 0 ? (
            <p>No chapters yet.</p>
          ) : (
            chapters.map((chapter) => (
              <div
                key={chapter.id}
                className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm"
              >
                <span>{chapter.title}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => goToUpdateChapter(chapter.id)}
                    className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Update
                  </button>
                  <button
                    onClick={() => deleteChapter(chapter.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
      <ToastContainer position="top-center" />
    </div>
  );
}
